import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import Button from "@/components/foundation/Button";
import { submitPartnerApplication } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[\d\s()+\-.]{7,}$/;
const URL_RE = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i;

const VOLUME_OPTIONS = [
  "Under $1M",
  "$1M – $5M",
  "$5M – $25M",
  "$25M – $100M",
  "$100M+",
];

/**
 * FundingPartnerForm — application form for funding partners.
 * Visual-only; Phase 4 will wire to POST /api/partners.
 *
 * Props:
 *   onSubmit?: (payload) => Promise<void> | void
 *   source: string (default "partners_page")
 */
export const FundingPartnerForm = ({
  onSubmit,
  source = "partners_page",
  className,
  "data-testid": testId = "funding-partner-form",
}) => {
  const [values, setValues] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    website: "",
    volume: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((e2) => ({ ...e2, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!values.companyName.trim()) next.companyName = "Company name is required.";
    if (!values.contactName.trim()) next.contactName = "Contact name is required.";
    if (!values.email.trim()) next.email = "Email is required.";
    else if (!EMAIL_RE.test(values.email.trim())) next.email = "Enter a valid email.";
    if (!values.phone.trim()) next.phone = "Phone number is required.";
    else if (!PHONE_RE.test(values.phone.trim())) next.phone = "Enter a valid phone number.";
    if (values.website.trim() && !URL_RE.test(values.website.trim()))
      next.website = "Enter a valid URL (or leave blank).";
    if (!values.volume) next.volume = "Please select a funding volume.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting" || status === "success") return;
    if (!validate()) return;

    setStatus("submitting");
    const payload = {
      company_name: values.companyName.trim(),
      contact_name: values.contactName.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      website: values.website.trim() || null,
      monthly_volume: values.volume,
      notes: values.notes.trim() || null,
      source,
    };
    try {
      if (onSubmit) {
        await onSubmit(payload);
      } else {
        await submitPartnerApplication(payload);
      }
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setErrors({ _root: err?.message || "Something went wrong. Please try again in a moment." });
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className={cn(
          "flex flex-col items-start gap-4 rounded-2xl border border-brand-emerald/25 bg-brand-emerald/8 p-8",
          className,
        )}
        data-testid="form-success"
        role="status"
        aria-live="polite"
      >
        <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-brand-emerald/15">
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 300, damping: 18 }}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-emerald text-black"
          >
            <Check className="h-5 w-5" strokeWidth={3} />
          </motion.span>
        </div>
        <div>
          <h3 className="font-display text-2xl font-semibold text-white">
            Application received.
          </h3>
          <p className="mt-2 text-sm text-text-secondary">
            Our team will reach out within 2 business days.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className={cn("w-full", className)}
      data-testid={testId}
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FieldInput
          label="Company Name"
          required
          value={values.companyName}
          onChange={set("companyName")}
          error={errors.companyName}
          testId="fp-company-name"
        />
        <FieldInput
          label="Contact Name"
          required
          value={values.contactName}
          onChange={set("contactName")}
          error={errors.contactName}
          testId="fp-contact-name"
        />
        <FieldInput
          label="Email"
          type="email"
          required
          value={values.email}
          onChange={set("email")}
          error={errors.email}
          testId="fp-email"
        />
        <FieldInput
          label="Phone"
          type="tel"
          required
          value={values.phone}
          onChange={set("phone")}
          error={errors.phone}
          testId="fp-phone"
        />
        <FieldInput
          label="Company Website"
          type="url"
          placeholder="https://example.com"
          value={values.website}
          onChange={set("website")}
          error={errors.website}
          testId="fp-website"
        />
        <FieldSelect
          label="Average Monthly Funding Volume"
          required
          value={values.volume}
          onChange={set("volume")}
          error={errors.volume}
          options={VOLUME_OPTIONS}
          testId="fp-volume"
        />
        <FieldTextarea
          label="Notes"
          value={values.notes}
          onChange={set("notes")}
          error={errors.notes}
          className="sm:col-span-2"
          testId="fp-notes"
        />
      </div>

      <AnimatePresence>
        {errors._root && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mt-4 text-xs text-red-400"
            role="alert"
          >
            {errors._root}
          </motion.p>
        )}
      </AnimatePresence>

      <div className="mt-8 flex items-center justify-between gap-4">
        <p className="text-xs text-text-muted">
          We&rsquo;ll never share your information. See our{" "}
          <a href="/privacy" className="underline hover:text-brand-emerald">
            Privacy Policy
          </a>
          .
        </p>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={status === "submitting"}
          rightIcon={status === "submitting" ? null : <ArrowRight className="h-4 w-4" />}
          data-testid="fp-submit-btn"
        >
          {status === "submitting" ? "Submitting…" : "Submit Application"}
        </Button>
      </div>
    </form>
  );
};

/* ----------------- Field helpers ----------------- */

const labelCls =
  "mb-1.5 block text-[11px] font-medium uppercase tracking-[0.12em] text-text-muted";

const controlCls = (hasError) =>
  cn(
    "w-full rounded-xl border bg-white/[0.02] px-4 py-3 text-sm text-white placeholder:text-text-muted",
    "outline-none transition-colors duration-200",
    "focus:border-brand-emerald/60 focus:bg-white/[0.04]",
    hasError
      ? "border-red-500/60"
      : "border-white/[0.08] hover:border-white/[0.16]",
  );

const errCls = "mt-1.5 pl-1 text-xs text-red-400";

const FieldInput = ({
  label,
  required,
  type = "text",
  value,
  onChange,
  error,
  placeholder,
  className,
  testId,
}) => (
  <label className={cn("flex flex-col", className)}>
    <span className={labelCls}>
      {label}
      {required && <span className="text-brand-emerald"> *</span>}
    </span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      aria-invalid={Boolean(error)}
      className={controlCls(Boolean(error))}
      data-testid={testId}
    />
    {error && <span className={errCls}>{error}</span>}
  </label>
);

const FieldSelect = ({
  label,
  required,
  value,
  onChange,
  error,
  options,
  className,
  testId,
}) => (
  <label className={cn("flex flex-col", className)}>
    <span className={labelCls}>
      {label}
      {required && <span className="text-brand-emerald"> *</span>}
    </span>
    <select
      value={value}
      onChange={onChange}
      aria-invalid={Boolean(error)}
      className={cn(controlCls(Boolean(error)), "appearance-none pr-8")}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7' viewBox='0 0 12 7'%3E%3Cpath fill='none' stroke='%2371717A' stroke-width='1.5' d='M1 1l5 5 5-5'/%3E%3C/svg%3E\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 1rem center",
      }}
      data-testid={testId}
    >
      <option value="" disabled className="bg-bg-raised">
        Select an option
      </option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-bg-raised">
          {o}
        </option>
      ))}
    </select>
    {error && <span className={errCls}>{error}</span>}
  </label>
);

const FieldTextarea = ({
  label,
  required,
  value,
  onChange,
  error,
  className,
  testId,
  rows = 4,
}) => (
  <label className={cn("flex flex-col", className)}>
    <span className={labelCls}>
      {label}
      {required && <span className="text-brand-emerald"> *</span>}
    </span>
    <textarea
      value={value}
      onChange={onChange}
      rows={rows}
      aria-invalid={Boolean(error)}
      className={cn(controlCls(Boolean(error)), "resize-none")}
      data-testid={testId}
    />
    {error && <span className={errCls}>{error}</span>}
  </label>
);

export default FundingPartnerForm;
